import React, {useEffect} from 'react';

import {FieldContainer} from './FieldContainer';

import {
    applyVisibilityRules,
    fieldValue,
    fieldState,
    filterDisplayFields,
    getComponentForField, isComponentValidForField, formComponentHandleIsBusy, formComponentHandleOnChange, formComponentHandleBlur
} from '../_helpers/form';

import {
    Field,
    FieldState,
    Form as FormType,
    Step as StepType,
    StepDisplayFieldMode
} from '../types';

export type FormStateCallback = (formState: FormState) => FormState;
export type FormProps = {
    form: FormType;
    step?: StepType;
    displayOnly?: boolean;
    highlightEditableFields?: boolean;
    editableFieldIds?: number[];
    showAdministrativeFields?: boolean;
    formState: FormState;
    setFormState: (formState: FormState | FormStateCallback) => void;

    displayFields?: undefined | number[];
    displayFieldsMode?: undefined | StepDisplayFieldMode;
    fieldContainerComponent?: undefined | any;

    showPrivate: boolean | undefined;

    locale?: string;
};

export type FormState = {
    fieldValues: any;
    fieldState: any;
    isValidating: boolean;
    initialValues?: any;
    visibility: any;
    busyFields: any; // map of fields that are "working" and should block form submission
};

export const initialFormState: FormState = {
    fieldValues: {},
    fieldState: {},
    isValidating: false,
    visibility: {},
    busyFields: {},
};

export const initialFieldState: FieldState = {
    touched: false,
    pristine: true,
    validating: false,
    valid: false,
    invalid: false,
    scrollTo: false,
    errors: [],
};

// @ts-ignore
export const Form: React.FunctionComponent<FormProps> = ({
                                                             form,
                                                             step,
                                                             displayOnly = false,
                                                             showAdministrativeFields = false,
                                                             highlightEditableFields = false,
                                                             editableFieldIds = [],
                                                             formState,
                                                             setFormState,
                                                             displayFields = [],
                                                             displayFieldsMode,
                                                             fieldContainerComponent = '',
                                                             showPrivate = false,
                                                             locale = 'en-US'
                                                         }): JSX.Element[] => {

    // whenever the fieldValue state changes, update visibility state
    useEffect(() => {
        const visibilityState = applyVisibilityRules(form, step, formState, showAdministrativeFields, showPrivate);
        setFormState({
            ...formState,
            visibility: visibilityState
        });
    }, [formState.fieldValues]);

    const handleIsBusy = formComponentHandleIsBusy(setFormState);
    const handleChange = formComponentHandleOnChange(form, setFormState);
    const handleBlur = formComponentHandleBlur(form, formState, setFormState, step, showAdministrativeFields, showPrivate);
    const FieldContainerComponent = fieldContainerComponent ? fieldContainerComponent : FieldContainer;

    return form.fieldsGroupedByLayoutContainers.map((container: any) => {
        const LayoutWrapper = container.name === 'default' ? React.Fragment : 'div';
        const layoutWrapperProps = container.name === 'default'
            ? {}
            : {
                className: container?.definition?.classes || '',
                style: container?.definition?.styles || {}
            };

        return (
            <LayoutWrapper key={container.name} {...layoutWrapperProps}>
                {container.fields.filter(filterDisplayFields(displayFieldsMode, displayFields)).map((field: Field) => {
                    const FormComponent = getComponentForField(field);
                    const shouldShow = formState.visibility.hasOwnProperty(field.name) && formState.visibility[field.name];

                    return (
                        (showAdministrativeFields || field.visibility !== 'administrative') && isComponentValidForField(field) &&
                        <FieldContainerComponent
                            key={field.key}
                            field={field}
                            fieldState={fieldState(field, formState)}
                            visible={shouldShow}
                            /* tslint:disable-next-line:triple-equals */
                            editableHighlight={displayOnly && highlightEditableFields && editableFieldIds.find((fieldId: number) => fieldId == field.id) !== undefined}
                            /* tslint:disable-next-line:triple-equals */
                            displayOnly={displayOnly && editableFieldIds.find((fieldId: number) => fieldId == field.id) === undefined}
                            locale={locale}
                        >
                            {fieldValue(formState, field) !== undefined &&
                                <FormComponent
                                    field={field}
                                    fieldState={fieldState(field, formState)}
                                    onChange={handleChange(field)}
                                    onBlur={handleBlur(field)}
                                    value={fieldValue(formState, field)}
                                    setIsBusy={handleIsBusy(field)}
                                    /* tslint:disable-next-line:triple-equals */
                                    displayOnly={displayOnly && editableFieldIds.find((fieldId: number) => fieldId == field.id) === undefined}

                                    // nested form component needs access to parent state for setting defaults....
                                    formState={formState}
                                    form={form}
                                    showAdministrativeFields={showAdministrativeFields}
                                    showPrivateFields={showPrivate}
                                    locale={locale}
                                />
                            }
                        </FieldContainerComponent>
                    )
                })}
            </LayoutWrapper>
        )
    });
};